import {
    computedValue,
    encounterCreatureField,
    encounterField,
    itemsCol,
    layerOf,
    type EncounterArr,
    type EncounterCreatureRow,
} from '../shared/array-types';

type Ft3dCombatRuntime = Window & {
    isCreatureVisibleInCombatWidget?: (row: EncounterCreatureRow) => boolean;
    routeCombatantClick?: (entityId: unknown, source?: string) => boolean;
    ftGetTargetSelection?: () => string;
    ftRenderFt3dCombatStrip?: (mount: HTMLElement | null, encounter: EncounterArr | null | undefined) => Promise<void>;
};

function selectedEntityIds(runtime: Ft3dCombatRuntime): Set<string> {
    const raw = String(runtime.ftGetTargetSelection?.() ?? '').trim();
    if (!raw) return new Set();
    const parts = raw.split(',').map(value => value.trim()).filter(Boolean);
    if (parts[0]?.includes('|')) parts.shift();
    return new Set(parts.map(value => value.toUpperCase()));
}

function combatPicture(row: EncounterCreatureRow): string {
    try {
        return String(pictureField0(selectCreaturePicture(row, { use_picture_thumbnail: true }), 'pic_url') ?? '');
    } catch {
        return '';
    }
}

function combatName(row: EncounterCreatureRow): string {
    try {
        return String(getCreatureName(row) || 'Creature');
    } catch {
        return 'Creature';
    }
}

function vitalityPercent(row: EncounterCreatureRow, key: string, fallback: number): number {
    const value = Number(computedValue(row, 'vitality', key) ?? fallback);
    return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : fallback;
}

function setHoverTooltip(element: HTMLElement, label: string): void {
    // template.js lazily creates these Bootstrap tooltips through its delegated mouseover handler.
    // Keep `title` absent so the browser's native tooltip cannot stack with the site tooltip.
    element.setAttribute('data-bs-toggle', 'tooltip');
    element.setAttribute('data-bs-placement', 'top');
    element.setAttribute('data-bs-animation', 'false');
    element.setAttribute('data-bs-original-title', label);
}

function normalizedCombatId(value: unknown): string {
    return String(value ?? '').trim().toUpperCase();
}

function selectCombatMembers(
    rows: EncounterCreatureRow[],
    currentTurnId: string,
    encounterActiveCombatId: unknown,
): EncounterCreatureRow[] {
    const activeCombatId = normalizedCombatId(encounterActiveCombatId)
        || normalizedCombatId(encounterCreatureField(
            rows.find(row => normalizedCombatId(
                encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'),
            ) === normalizedCombatId(currentTurnId))?.encounter_creatures,
            'combat_id',
        ));
    const hasTaggedMembership = rows.some(row => (
        normalizedCombatId(encounterCreatureField(row.encounter_creatures, 'combat_id')) !== ''
    ));
    // Open combats created before the combat-membership migration have in_combat/current-turn
    // truth but no active_combat_id and no creature combat_id stamps. Preserve their historical
    // visible initiative roster until that epoch ends. Once ANY membership tag exists, fail closed:
    // never mix an untagged row into a tagged combat or infer membership from mere co-location.
    const members = activeCombatId !== ''
        ? rows.filter(row => (
            normalizedCombatId(encounterCreatureField(row.encounter_creatures, 'combat_id')) === activeCombatId
        ))
        : (hasTaggedMembership ? [] : [...rows]);
    return members.sort((left, right) => (
        Number(encounterCreatureField(right.encounter_creatures, 'creature_initiative') ?? 0)
        - Number(encounterCreatureField(left.encounter_creatures, 'creature_initiative') ?? 0)
    ));
}

function appendPortrait(parent: HTMLElement, row: EncounterCreatureRow, name: string): void {
    const picture = combatPicture(row);
    if (picture) {
        const image = document.createElement('img');
        image.src = picture;
        image.alt = name;
        image.draggable = false;
        image.style.objectFit = 'contain';
        parent.appendChild(image);
        return;
    }
    const initials = document.createElement('span');
    initials.className = 'ft3d-combat-strip__initials';
    initials.textContent = name.slice(0, 2);
    parent.appendChild(initials);
}

function isLairActor(row: EncounterCreatureRow): boolean {
    const itemType = encounterCreatureField(row.encounter_creatures, 'item_type')
        ?? itemsCol(row, 'item_type');
    return String(itemType ?? '').toLowerCase() === 'lair';
}

function appendLairIcon(parent: HTMLElement): void {
    const icon = document.createElement('span');
    icon.className = 'ft3d-combat-strip__lair-icon';
    icon.setAttribute('aria-hidden', 'true');
    try {
        icon.innerHTML = typeof iconMap === 'function' ? iconMap('lair', 28, 28, false) : '';
    } catch {
        icon.replaceChildren();
    }
    if (icon.childElementCount > 0) {
        parent.appendChild(icon);
        return;
    }
    const fallback = document.createElement('span');
    fallback.className = 'ft3d-combat-strip__initials';
    fallback.textContent = 'L';
    parent.appendChild(fallback);
}

function buildLegendaryChip(row: EncounterCreatureRow): HTMLElement {
    const thumb = document.createElement('div');
    thumb.className = 'ft3d-combat-strip__legendary-thumb';
    appendPortrait(thumb, row, combatName(row));
    const badge = document.createElement('span');
    badge.className = 'ft3d-combat-strip__legendary-ap';
    thumb.appendChild(badge);
    return thumb;
}

/**
 * How one combatant appears in the strip for THIS viewer (owner ruling 2026-10-01, lane N8).
 *   'shown'       — the page's verdict (`isCreatureVisibleInCombatWidget`) lets the viewer see it: portrait chip.
 *   'unperceived' — the viewer cannot perceive it (the controlled caster's perceived-id set, or the darkness
 *                   rule for a player): it KEEPS its initiative slot as a black "?" box, so the strip never
 *                   re-flows as perception changes and an unseen actor's turn still shows. The box carries
 *                   nothing of the creature (no name, portrait, HP, number, team, tooltip or id) and does nothing
 *                   on click. The viewer is the caster the page controls; a GM controlling no caster has no
 *                   perceived set and sees everything.
 *   null          — not in the strip at all: dead, or a private (non-lair) row. Privacy is not perception: those
 *                   rows were never in anyone's strip and stay out.
 */
type StripPresence = 'shown' | 'unperceived';

function stripPresence(runtime: Ft3dCombatRuntime, row: EncounterCreatureRow): StripPresence | null {
    if (Number(encounterCreatureField(row.encounter_creatures, 'encounter_creature_dead') ?? 0) === 1) return null;
    if (runtime.isCreatureVisibleInCombatWidget?.(row) !== false) return 'shown';
    if (Number(encounterCreatureField(row.encounter_creatures, 'encounter_creature_private') ?? 0) === 1) return null;
    return 'unperceived';
}

function appendUnperceivedThumb(parent: HTMLElement, isCurrent: boolean): void {
    // A plain div with no listener and no `data-encounter_creature_id`: the delegated strip click
    // (targeting-combat.ts targetObject) finds no id and stops, so no zoom, target or select.
    const thumb = document.createElement('div');
    thumb.className = 'ft3d-combat-strip__thumb is-unperceived';
    thumb.classList.toggle('is-current', isCurrent);
    thumb.dataset.unperceived = '1';
    thumb.setAttribute('role', 'listitem');
    thumb.setAttribute('aria-label', 'Unseen combatant');
    const mark = document.createElement('span');
    mark.className = 'ft3d-combat-strip__unknown';
    mark.setAttribute('aria-hidden', 'true');
    mark.textContent = '?';
    thumb.appendChild(mark);
    parent.appendChild(thumb);
}

type HeldChip = { element: HTMLElement; presence: StripPresence; lair: boolean };
type HeldStrip = {
    encounter: EncounterArr;
    strip: HTMLElement;
    chips: Map<string, HeldChip>;
    legendary: HTMLElement;
    legendaryChips: Map<string, HTMLElement>;
};
const renderedStrips = new WeakMap<HTMLElement, HeldStrip>();

function toggleClass(element: HTMLElement, name: string, value: boolean): void {
    if (element.classList.contains(name) !== value) element.classList.toggle(name, value);
}

function patchHeight(element: HTMLElement | null, value: number): void {
    if (element && element.style.height !== `${value}%`) element.style.height = `${value}%`;
}

function buildCombatChip(runtime: Ft3dCombatRuntime, row: EncounterCreatureRow, presence: StripPresence): HTMLElement {
    const holder = document.createElement('div');
    if (presence === 'unperceived') {
        appendUnperceivedThumb(holder, false);
        return holder.firstElementChild as HTMLElement;
    }
    const id = String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '');
    // A lair encounter actor is identified by its own item_type. The `lair` layer belongs to
    // creatures that reference a lair template, so using it here misclassified the actual
    // private lair row as an ordinary creature and replaced its generic Lair icon with a portrait.
    const isLair = isLairActor(row);
    // Keep the initiative fact while withholding the private catalog identity. The lair's item
    // name and portrait are not needed to communicate that an environmental turn exists.
    const name = isLair ? 'Lair' : combatName(row);
    const thumb = document.createElement(isLair ? 'div' : 'button');
    if (thumb instanceof HTMLButtonElement) thumb.type = 'button';
    thumb.className = 'ft3d-combat-strip__thumb';
    thumb.dataset.encounter_creature_id = id;
    setHoverTooltip(thumb, name);
    thumb.setAttribute('role', 'listitem');
    if (!isLair) {
        thumb.addEventListener('click', () => runtime.routeCombatantClick?.(id, 'ft3d-combat-strip'));
    } else {
        thumb.classList.add('is-lair');
    }
    if (isLair) appendLairIcon(thumb);
    else appendPortrait(thumb, row, name);

    if (!isLair) {
        const damage = document.createElement('span');
        damage.className = 'ft3d-combat-strip__damage';
        damage.style.height = `${100 - vitalityPercent(row, 'v_bar_v_percent', 100)}%`;
        thumb.appendChild(damage);
        const temporary = vitalityPercent(row, 'v_bar_v_temp_percent', 0);
        if (temporary > 0) {
            const temp = document.createElement('span');
            temp.className = 'ft3d-combat-strip__temporary';
            temp.style.height = `${temporary}%`;
            thumb.appendChild(temp);
        }
        const number = Number(encounterCreatureField(row.encounter_creatures, 'creature_number') ?? 0) || 0;
        if (number > 0) {
            const badge = document.createElement('span');
            badge.className = 'ft3d-combat-strip__number';
            badge.textContent = String(number);
            thumb.appendChild(badge);
        }
    }
    return thumb;
}

function patchPortrait(thumb: HTMLElement, row: EncounterCreatureRow, name: string): void {
    let image = thumb.querySelector('img');
    const picture = combatPicture(row);
    if ((picture && !image) || (!picture && image)) {
        image?.remove();
        thumb.querySelector('.ft3d-combat-strip__initials')?.remove();
        appendPortrait(thumb, row, name);
        image = thumb.querySelector('img');
    }
    if (image && image.getAttribute('src') !== picture) image.src = picture;
    if (image && image.alt !== name) image.alt = name;
    const initials = thumb.querySelector('.ft3d-combat-strip__initials');
    if (initials && initials.textContent !== name.slice(0, 2)) initials.textContent = name.slice(0, 2);
}

function patchChip(held: HeldChip, row: EncounterCreatureRow, current: boolean, targeted: boolean): void {
    const thumb = held.element;
    toggleClass(thumb, 'is-current', current);
    if (held.presence === 'unperceived') return;
    toggleClass(thumb, 'is-targeted', targeted);
    const name = held.lair ? 'Lair' : combatName(row);
    if (thumb.getAttribute('data-bs-original-title') !== name) setHoverTooltip(thumb, name);
    if (held.lair) return;
    patchPortrait(thumb, row, name);
    patchHeight(thumb.querySelector('.ft3d-combat-strip__damage'), 100 - vitalityPercent(row, 'v_bar_v_percent', 100));
    const temporary = vitalityPercent(row, 'v_bar_v_temp_percent', 0);
    let temp = thumb.querySelector<HTMLElement>('.ft3d-combat-strip__temporary');
    if (temporary > 0 && !temp) {
        temp = document.createElement('span');
        temp.className = 'ft3d-combat-strip__temporary';
        thumb.appendChild(temp);
    }
    if (temporary === 0) temp?.remove();
    else patchHeight(temp, temporary);
    const number = Number(encounterCreatureField(row.encounter_creatures, 'creature_number') ?? 0) || 0;
    let badge = thumb.querySelector('.ft3d-combat-strip__number');
    if (number > 0 && !badge) {
        badge = document.createElement('span');
        badge.className = 'ft3d-combat-strip__number';
        thumb.appendChild(badge);
    }
    if (number <= 0) badge?.remove();
    else if (badge && badge.textContent !== String(number)) badge.textContent = String(number);
}

function syncCombatStrip(mount: HTMLElement, encounter: EncounterArr, changedIds?: ReadonlySet<string>, turnId?: string): void {
    const runtime = window as Ft3dCombatRuntime;
    const inCombat = Number(encounterField(encounter, 'in_combat') ?? 0) === 1;
    const held = renderedStrips.get(mount);
    if (!inCombat) {
        if (!mount.hidden) mount.hidden = true;
        if (mount.childNodes.length) mount.replaceChildren();
        renderedStrips.delete(mount);
        return;
    }
    const presence = new Map<EncounterCreatureRow, StripPresence>();
    for (const row of encounter.encounter_creatures_arr ?? []) {
        const verdict = stripPresence(runtime, row);
        if (verdict) presence.set(row, verdict);
    }
    const currentTurnId = normalizedCombatId(turnId ?? encounterField(encounter, 'current_encounter_creature_id_turn'));
    const members = selectCombatMembers([...presence.keys()], currentTurnId, encounterField(encounter, 'active_combat_id'));
    if (!members.length) {
        if (mount.childNodes.length) mount.replaceChildren();
        renderedStrips.delete(mount);
        if (!mount.hidden) mount.hidden = true;
        return;
    }
    if (mount.hidden) mount.hidden = false;
    let state = held;
    if (!state || state.strip.parentElement !== mount) {
        const strip = document.createElement('div');
        strip.className = 'ft3d-combat-strip';
        strip.setAttribute('role', 'list');
        strip.setAttribute('aria-label', 'Combat initiative');
        const legendary = document.createElement('div');
        legendary.className = 'ft3d-combat-strip__legendary';
        state = { encounter, strip, legendary, chips: new Map(), legendaryChips: new Map() };
        mount.replaceChildren(strip);
        renderedStrips.set(mount, state);
    }
    state.encounter = encounter;
    const selected = selectedEntityIds(runtime);
    const legendaryPhase = String(encounterField(encounter, 'turn_phase') ?? '') === 'legendary';
    const wanted = new Set<string>();
    const wantedLegendary = new Set<string>();
    let position = 0;
    let legendaryPosition = 0;
    for (const row of members) {
        const id = normalizedCombatId(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
        if (!id) continue;
        wanted.add(id);
        const verdict = presence.get(row)!;
        const lair = isLairActor(row);
        let chip = state.chips.get(id);
        const created = !chip || chip.presence !== verdict || chip.lair !== lair;
        if (created) {
            chip?.element.remove();
            chip = { element: buildCombatChip(runtime, row, verdict), presence: verdict, lair };
            state.chips.set(id, chip);
        }
        if (!chip) continue;
        if (created || !changedIds || changedIds.has(id)) {
            patchChip(chip, row, id === currentTurnId && (verdict === 'unperceived' || !legendaryPhase), selected.has(id));
        } else {
            toggleClass(chip.element, 'is-current', id === currentTurnId && (verdict === 'unperceived' || !legendaryPhase));
            if (verdict === 'shown') toggleClass(chip.element, 'is-targeted', selected.has(id));
        }
        if (state.strip.children[position] !== chip.element) state.strip.insertBefore(chip.element, state.strip.children[position] ?? null);
        position++;
        if (verdict !== 'shown'
            || encounterCreatureField(row.encounter_creatures, 'controller_encounter_creature_id')
            || Number(encounterCreatureField(row.encounter_creatures, 'encounter_creature_private') ?? 0) === 1
            || !layerOf(row, 'legendary_creature')) continue;
        wantedLegendary.add(id);
        let legendaryChip = state.legendaryChips.get(id);
        if (!legendaryChip) {
            legendaryChip = buildLegendaryChip(row);
            state.legendaryChips.set(id, legendaryChip);
        }
        patchPortrait(legendaryChip, row, combatName(row));
        const aps = Number(encounterCreatureField(row.encounter_creatures, 'aps') ?? 0) || 0;
        toggleClass(legendaryChip, 'is-current', legendaryPhase && id === currentTurnId);
        toggleClass(legendaryChip, 'is-forfeit', aps <= 0);
        const label = aps > 0 ? `${combatName(row)} — Legendary Turns: ${aps} AP left` : `${combatName(row)} — Legendary Turns forfeit`;
        if (legendaryChip.getAttribute('data-bs-original-title') !== label) setHoverTooltip(legendaryChip, label);
        const badge = legendaryChip.querySelector('.ft3d-combat-strip__legendary-ap');
        if (badge && badge.textContent !== String(aps)) badge.textContent = String(aps);
        if (state.legendary.children[legendaryPosition] !== legendaryChip) state.legendary.insertBefore(legendaryChip, state.legendary.children[legendaryPosition] ?? null);
        legendaryPosition++;
    }
    for (const [id, chip] of state.chips) if (!wanted.has(id)) { chip.element.remove(); state.chips.delete(id); }
    for (const [id, chip] of state.legendaryChips) if (!wantedLegendary.has(id)) { chip.remove(); state.legendaryChips.delete(id); }
    if (wantedLegendary.size && state.legendary.parentElement !== state.strip) state.strip.appendChild(state.legendary);
    else if (!wantedLegendary.size) state.legendary.remove();
}

async function renderFt3dCombatStrip(mount: HTMLElement | null, encounter: EncounterArr | null | undefined): Promise<void> {
    if (!mount || !encounter) return;
    const held = renderedStrips.get(mount);
    const mounting = !held || held.strip.parentElement !== mount;
    syncCombatStrip(mount, encounter);
    if (mounting && renderedStrips.has(mount)) await loadPostFunctions(false);
}

/** Patch a held strip without building chips for unchanged members. Mount/context changes use render. */
function patchCombatStrip(changedIds: readonly string[], turnId?: string, mount?: HTMLElement): void {
    const ids = new Set(changedIds.map(normalizedCombatId));
    const mounts = mount ? [mount] : Array.from(document.querySelectorAll<HTMLElement>('.ft3d-combat-strip')).map(strip => strip.parentElement!);
    for (const target of mounts) {
        const state = renderedStrips.get(target);
        if (state) syncCombatStrip(target, state.encounter, ids, turnId);
    }
}
Object.assign(window, { ftRenderFt3dCombatStrip: renderFt3dCombatStrip, patchCombatStrip });
export { renderFt3dCombatStrip, patchCombatStrip, selectCombatMembers };
